/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { ApolloError, gql, useLazyQuery, useMutation } from "@apollo/client";
import { handleApolloError } from "../Shared/Errors";
import searchFilesGql from "../Api/Gql/SearchFiles";
import listFoldersGql from "../Api/Gql/ListFolders";
import filesInFolderGql from "../Api/Gql/FilesInFolder";
import createFolderGql from "../Api/Gql/CreateFolder";
import renameFolderGql from "../Api/Gql/RenameFolder";
import deleteFolderGql from "../Api/Gql/DeleteFolder";
import moveFilesToFolderGql from "../Api/Gql/MoveFilesToFolder";
import copyFilesToFolderGql from "../Api/Gql/CopyFilesToFolder";
import listAllFoldersGql from "../Api/Gql/ListAllFolders";
import moveFoldersGql from "../Api/Gql/MoveFolders";
import updateFileGql from "../Api/Gql/UpdateFile";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { UUID } from "../Utils/Types";
import { DeleteConfirmationModal } from "../Shared/DeleteConfirmationModal";
import removeFilesGql from "../Api/Gql/RemoveFiles";
import { loggedNotify, Component } from "../Shared/Notify";
import { AxiosContext } from "../Utils/AuthContext";
import { FileInfo, FileInfoInput, FileType, Folder } from "Api";
import { OverflowMenu } from "../Shared/OverflowMenu";
import { FiFolderPlus, FiUpload, FiX } from "react-icons/fi";
import EmptyPageComponent, {
  EmptyStateButton,
} from "../Shared/PageComponents/EmptyPageComponent";
import { UploadModal, UploadedFile } from "../Shared/Upload/UploadModal";
import { useDexterFileUpload } from "../Shared/Upload/UseDexterFileUpload";
import { NamePromptModal } from "./NamePromptModal";
import { MoveToModal, MoveToItem } from "./MoveToModal";
import FileSearchResultTable, {
  isListableFile,
  useResponsivePageSize,
} from "./FileSearchResultTable";
import FileTablePagination from "./FileTablePagination";
import TableActionBar from "./TableActionsBar";
import { TableSelectActionsBar } from "./TableSelectActionsBar";
import { Input } from "baseui/input";
import { Button } from "baseui/button";
import { Value } from "baseui/select";
import { getSecondaryButtonOverrides } from "../DesignSystem/ButtonColorStyles";
import { Search } from "baseui/icon";
import FileTypeModal from "../TransformEngine/Components/FileTypeModal";
import { NodeFileDataModal } from "../Shared/NodeFileDataModal";
import {
  Endpoints,
  fileDownload,
  fileTypeOptionsFilesTable,
  replaceExtension,
  useListMaxHeight,
} from "../Utils/Common";
import CopyToModal from "./CopyToModal";
import CopyFolders from "../Api/Gql/CopyFolders";

// Default page height
const INITIAL_PAGE_HEIGHT = 330;

// Keeps the action bar the same height whether it shows the default tools or the
// selection actions.
const ACTION_BAR_MIN_HEIGHT = "71px";

/** For going back one level in the folder breadcrumb trail. */
interface FolderCrumb {
  id: UUID;
  name: string;
}

// Remembers that the user dismissed the saved-mapping banner, so it only
// appears until it's acknowledged once.
const SAVED_MAPPING_BANNER_KEY = "dexter.files.savedMappingBannerDismissed";

// How many folders deep the breadcrumb goes before the middle collapses to "…".
// Keep this at 3 for now; we can adjust if necessary.
const BREADCRUMB_COLLAPSE_AFTER = 3;

const ROOT_NAME = "Files";

function describeItems(names: string[], noun: string): string {
  if (names.length === 1) return names[0];
  return `${names.length} ${noun}`;
}

/**
 * A clickable breadcrumb segment in the page header. Styled at the header's text
 * size and underlined on hover so it reads as a way to jump back up the tree.
 */
function HeaderCrumbButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: currentTheme.colors.defaultTextColor,
        fontFamily: currentTheme.fonts.primary,
        fontSize: currentTheme.fontSizes.large,
        textDecoration: hover ? "underline" : "none",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "220px",
      }}
    >
      {label}
    </button>
  );
}

type BreadcrumbSegment =
  | { kind: "crumb"; key: string; label: string; index: number }
  | { kind: "ellipsis" };

/**
 * The page-header breadcrumb: the "Files" root plus each folder the user has
 * navigated into. Clicking "Files" or an ancestor jumps to that level; the
 * current folder is bold and inert. When the trail gets long the earliest
 * folders collapse to a clickable "…" (which expands them) so it can't push the
 * header past its width.
 */
function HeaderBreadcrumb({
  folderPath,
  onNavigate,
}: {
  folderPath: FolderCrumb[];
  onNavigate: (index: number) => void;
}): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const [expanded, setExpanded] = React.useState(false);

  // Collapse again whenever the location changes.
  React.useEffect(() => {
    setExpanded(false);
  }, [folderPath]);

  const collapsed = !expanded && folderPath.length > BREADCRUMB_COLLAPSE_AFTER;

  const segments: BreadcrumbSegment[] = [
    { kind: "crumb", key: "root", label: ROOT_NAME, index: -1 },
  ];
  if (collapsed) {
    // Keep the root and the last two folders; hide the middle behind "…".
    segments.push({ kind: "ellipsis" });
    const base = folderPath.length - 2;
    folderPath.slice(-2).forEach((crumb, crumbIndex) =>
      segments.push({
        kind: "crumb",
        key: crumb.id,
        label: crumb.name,
        index: base + crumbIndex,
      })
    );
  } else {
    folderPath.forEach((crumb, crumbIndex) =>
      segments.push({
        kind: "crumb",
        key: crumb.id,
        label: crumb.name,
        index: crumbIndex,
      })
    );
  }

  const titleFont = {
    fontFamily: currentTheme.fonts.primary,
    fontSize: currentTheme.fontSizes.large,
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "6px",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        const separator =
          i > 0 ? (
            <span
              style={{
                color: currentTheme.colors.secondaryTextColor,
                ...titleFont,
              }}
            >
              /
            </span>
          ) : null;

        if (seg.kind === "ellipsis") {
          return (
            <React.Fragment key="ellipsis">
              {separator}
              <button
                type="button"
                title="Show full path"
                onClick={() => setExpanded(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: currentTheme.colors.secondaryTextColor,
                  ...titleFont,
                }}
              >
                …
              </button>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={seg.key}>
            {separator}
            {isLast ? (
              <span
                style={{
                  color: currentTheme.colors.defaultTextColor,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "260px",
                  ...titleFont,
                }}
              >
                {seg.label}
              </span>
            ) : (
              <HeaderCrumbButton
                label={seg.label}
                onClick={() => onNavigate(seg.index)}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * Download endpoints may return HTTP 200 even on failure, with the actual
 * error status and message in a JSON GeneralResponse.
 * Returns the error message if found, or null if the blob is a valid file.
 */
async function readDownloadErrorMessage(blob: Blob): Promise<string | null> {
  if (!blob.type.includes("application/json")) {
    return null;
  }

  try {
    const parsed = JSON.parse(await blob.text());

    if (
      !parsed ||
      typeof parsed.status !== "number" ||
      [0, 200].includes(parsed.status) ||
      typeof parsed.message !== "string"
    ) {
      return null;
    }

    return parsed.message || "You may not have access to this file.";
  } catch {
    // Not a recognized GeneralResponse; treat the blob as the file.
    return null;
  }
}

export function FileSearchPage(): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const axiosContext = React.useContext(AxiosContext);
  // /upload + removeFiles paths wiring injected into the upload modal.
  const { uploadFile, removeFile: removeUploadedFile } = useDexterFileUpload();
  const [searchValue, setSearchValue] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [checkedMap, setCheckedMap] = React.useState<Record<UUID, boolean>>({});
  const [deletePromptIsOpen, setDeletePromptIsOpen] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [folderModalOpen, setFolderModalOpen] = React.useState(false);
  // The folder currently being renamed (drives the rename modal); null = closed.
  const [renameTarget, setRenameTarget] = React.useState<Folder | null>(null);
  // The file currently being renamed (drives its rename modal); null = closed.
  const [renameFileTarget, setRenameFileTarget] =
    React.useState<FileInfo | null>(null);
  // The single "Move to" picker for any selection of files and/or folders.
  const [moveModalOpen, setMoveModalOpen] = React.useState(false);
  // The file whose contents are shown in the view modal; null = closed.
  const [viewingFile, setViewingFile] = React.useState<FileInfo | null>(null);
  const [copyModalOpen, setCopyModalOpen] = React.useState(false);
  const [fileTypeChangeModalOpen, setFileTypeChangeModalOpen] =
    React.useState<boolean>(false);
  const [pendingFileType, setPendingFileType] = React.useState<Value>(null);
  // Breadcrumb trail of the folders the user has navigated into. Empty = root.
  const [folderPath, setFolderPath] = React.useState<FolderCrumb[]>([]);
  // The saved-mapping banner shows until the user dismisses it (persisted).
  const [bannerDismissed, setBannerDismissed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(SAVED_MAPPING_BANNER_KEY) === "true";
    } catch {
      return false;
    }
  });

  const dismissBanner = React.useCallback(() => {
    setBannerDismissed(true);
    try {
      localStorage.setItem(SAVED_MAPPING_BANNER_KEY, "true");
    } catch {
      // Best-effort; the banner just won't persist its dismissal.
    }
  }, []);

  const currentFolderId: UUID | null =
    folderPath.length > 0 ? folderPath[folderPath.length - 1].id : null;
  const isSearching = searchValue.trim() !== "";

  const SEARCH_INPUT_OVERRIDES = {
    Root: {
      style: {
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
        borderTopColor: currentTheme.colors.primaryLight,
        borderRightColor: currentTheme.colors.primaryLight,
        borderBottomColor: currentTheme.colors.primaryLight,
        borderLeftColor: currentTheme.colors.primaryLight,
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        backgroundColor: currentTheme.colors.dropdownBackground,
        outline: "none",
        width: "15%",
        marginTop: "8px",
        height: "35px",
        marginLeft: "10px",
        marginBottom: "10px",
      },
    },
    InputContainer: {
      style: {
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      },
    },
    Input: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.text,
        fontFamily: currentTheme.fonts.primary,
        fontSize: currentTheme.fontSizes.medium,
        minWidth: 0,
      },
    },
    ClearIcon: {
      style: {
        color: currentTheme.colors.text,
        ":hover": {
          color: currentTheme.colors.primaryLight,
        },
      },
    },
    ClearIconContainer: {
      style: {
        backgroundColor: currentTheme.colors.inputNodeBackgroundColor,
        color: currentTheme.colors.text,
        flexShrink: 0,
      },
    },
    StartEnhancer: {
      style: {
        backgroundColor: "transparent",
        paddingRight: "0px",
      },
    },
  } as const;

  // Used to take into account different screen sizes. This will help determine
  // how many pages and rows to generate in the search results table
  const pageSize = useResponsivePageSize(INITIAL_PAGE_HEIGHT);

  const onFetchError = React.useCallback(
    // Apollo's onError provides an ApolloError.
    (error: ApolloError) => handleApolloError(error, "Error fetching files!"),
    []
  );

  // ── Queries ────────────────────────────────────────────────────────────────
  // Browse mode: the folders + files inside the current folder.
  const [
    loadFolders,
    { data: foldersData, refetch: refetchFolders, called: foldersCalled },
  ] = useLazyQuery(gql(listFoldersGql), {
    fetchPolicy: "network-only",
    onError: onFetchError,
  });
  const [
    loadFilesInFolder,
    {
      data: folderFilesData,
      loading: folderLoading,
      refetch: refetchFilesInFolder,
      called: filesCalled,
    },
  ] = useLazyQuery(gql(filesInFolderGql), {
    fetchPolicy: "network-only",
    onError: onFetchError,
  });
  // Search mode: a global, flat file search across every folder.
  const [
    runSearch,
    {
      data: searchData,
      loading: searchLoading,
      refetch: refetchSearch,
      called: searchCalled,
    },
  ] = useLazyQuery(gql(searchFilesGql), {
    fetchPolicy: "network-only",
    onError: onFetchError,
  });
  // Every folder, flat — the destinations for the Move to picker. Loaded on
  // mount and refreshed each time the picker opens.
  const [
    loadAllFolders,
    {
      data: allFoldersData,
      refetch: refetchAllFolders,
      called: allFoldersCalled,
    },
  ] = useLazyQuery(gql(listAllFoldersGql), {
    fetchPolicy: "network-only",
    onError: onFetchError,
  });

  const allFolders: Folder[] = React.useMemo(
    () => (allFoldersData?.listAllFolders as Folder[]) ?? [],
    [allFoldersData]
  );

  React.useEffect(() => {
    loadAllFolders();
  }, [loadAllFolders]);

  // Re-running a lazy query with the same (empty) variables may not hit the
  // network, so use refetch once it has run.
  const fetchAllFolders = React.useCallback(() => {
    if (allFoldersCalled) {
      refetchAllFolders().catch((error) =>
        handleApolloError(error as ApolloError, "Error fetching folders!")
      );
    } else {
      loadAllFolders();
    }
  }, [allFoldersCalled, refetchAllFolders, loadAllFolders]);

  // Re-fetch whatever the current view is. Uses refetch once a query has run,
  // since re-executing a lazy query with unchanged variables may not hit the network.
  const refreshView = React.useCallback(async () => {
    try {
      if (isSearching) {
        const vars = { nameSubstring: searchValue.trim() };
        await (searchCalled
          ? refetchSearch(vars)
          : runSearch({ variables: vars }));
        return;
      }
      const folderVars = { parentId: currentFolderId };
      const fileVars = { folderId: currentFolderId };
      await Promise.all([
        foldersCalled
          ? refetchFolders(folderVars)
          : loadFolders({ variables: folderVars }),
        filesCalled
          ? refetchFilesInFolder(fileVars)
          : loadFilesInFolder({ variables: fileVars }),
      ]);
    } catch (error) {
      handleApolloError(error as ApolloError, "Error refreshing files!");
    }
  }, [
    isSearching,
    searchValue,
    currentFolderId,
    searchCalled,
    foldersCalled,
    filesCalled,
    runSearch,
    refetchSearch,
    loadFolders,
    refetchFolders,
    loadFilesInFolder,
    refetchFilesInFolder,
  ]);

  // Browse the current folder whenever we're not searching (and on folder change).
  React.useEffect(() => {
    if (isSearching) {
      return;
    }
    loadFolders({ variables: { parentId: currentFolderId } });
    loadFilesInFolder({ variables: { folderId: currentFolderId } });
  }, [isSearching, currentFolderId, loadFolders, loadFilesInFolder]);

  // Debounced search effect
  React.useEffect(() => {
    if (!isSearching) {
      return undefined;
    }
    const debounceTimer = setTimeout(() => {
      runSearch({ variables: { nameSubstring: searchValue.trim() } });
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [isSearching, searchValue, runSearch]);

  const folders: Folder[] = React.useMemo(
    () => (isSearching ? [] : (foldersData?.listFolders as Folder[]) ?? []),
    [isSearching, foldersData]
  );

  const allFiles: FileInfo[] = React.useMemo(() => {
    const source = isSearching
      ? searchData?.searchFiles
      : folderFilesData?.filesInFolder;
    return (source as FileInfo[]) ?? [];
  }, [isSearching, searchData, folderFilesData]);

  // The folder a file lives in. While browsing, files without a folderId in the
  // response are known to be in the folder being viewed.
  const fileFolderId = React.useCallback(
    (file: FileInfo): UUID | null =>
      file.folderId ?? (isSearching ? null : currentFolderId),
    [isSearching, currentFolderId]
  );

  // The folder a folder lives in (its parent); same fallback as files.
  const folderParentId = React.useCallback(
    (folder: Folder): UUID | null =>
      folder.parentId ?? (isSearching ? null : currentFolderId),
    [isSearching, currentFolderId]
  );

  const loading = isSearching ? searchLoading : folderLoading;

  const browseLoaded =
    foldersData !== undefined && folderFilesData !== undefined;

  // Count only what the table actually shows (folders + listable files).
  const listableCount = React.useMemo(
    () => folders.length + allFiles.filter(isListableFile).length,
    [folders, allFiles]
  );

  // The "No files yet" upload CTA only makes sense at the empty root.
  const showEmptyState =
    !isSearching &&
    !loading &&
    browseLoaded &&
    listableCount === 0 &&
    folderPath.length === 0;

  const tableEmptyMessage = loading
    ? "Loading…"
    : isSearching
    ? "No files match the search text"
    : "No files in this folder. Use the upload button to add files.";

  const pageCount = Math.max(1, Math.ceil(listableCount / pageSize));

  // On resize, stay on the page containing the row that was at the top
  const prevPageSizeRef = React.useRef(pageSize);
  React.useEffect(() => {
    const prev = prevPageSizeRef.current;
    if (prev === pageSize) return;
    prevPageSizeRef.current = pageSize;
    setCurrentPage((page) => Math.floor(((page - 1) * prev) / pageSize) + 1);
  }, [pageSize]);

  // Never point past the last page (resizes, deletions, new search results)
  const safePage = Math.min(currentPage, pageCount);

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSortChange = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleCheckedMapChange = React.useCallback(
    (map: Record<UUID, boolean>) => {
      setCheckedMap(map);
    },
    []
  );

  // Split the selection into files and folders so each is handled with the
  // right mutation.
  const fileIdSet = React.useMemo(
    () => new Set(allFiles.map((file) => file.id)),
    [allFiles]
  );
  const folderIdSet = React.useMemo(
    () => new Set(folders.map((folder) => folder.id)),
    [folders]
  );
  const checkedIds = React.useMemo(
    () => Object.keys(checkedMap).filter((id) => checkedMap[id]),
    [checkedMap]
  );
  const checkedFileIds = React.useMemo(
    () => checkedIds.filter((id) => fileIdSet.has(id as UUID)),
    [checkedIds, fileIdSet]
  );
  const checkedFolderIds = React.useMemo(
    () => checkedIds.filter((id) => folderIdSet.has(id as UUID)),
    [checkedIds, folderIdSet]
  );
  const selectedCount = checkedFileIds.length + checkedFolderIds.length;

  // The selected files (not folders), for the file type and move modals.
  const checkedFiles = React.useMemo(() => {
    const ids = new Set(checkedFileIds);
    return allFiles.filter((file) => ids.has(String(file.id)));
  }, [allFiles, checkedFileIds]);

  // The selected folders
  const checkedFolders = React.useMemo(() => {
    const ids = new Set(checkedFolderIds);
    return folders.filter((folder) => ids.has(String(folder.id)));
  }, [folders, checkedFolderIds]);

  // ── Change file type ───────────────────────────────────────────────────────
  const [updateFileMutation] = useMutation(gql(updateFileGql));

  const handleFormatChange = React.useCallback(
    async (newFormat: string, changedIds: string[]) => {
      const newExtension =
        fileTypeOptionsFilesTable.find((option) => option.id === newFormat)
          ?.extension ?? "";
      const filesById = new Map(
        allFiles.map((file) => [String(file.id), file])
      );

      const results = await Promise.allSettled(
        changedIds.map((fileId) => {
          const currentFileInfo = filesById.get(fileId);
          const currentName = currentFileInfo?.attributes?.name;

          // Only rename when we know both the current name and the new extension.
          const newName =
            currentName && newExtension
              ? replaceExtension(currentName, newExtension)
              : undefined;

          const newFileInfo: FileInfoInput = {
            id: fileId,
            securityMarkings: currentFileInfo?.securityMarkings,
            predictedFileType: currentFileInfo?.predictedFileType,
            userSelectedFileType:
              newFormat as FileInfoInput["userSelectedFileType"],
            ...(newName && newName !== currentName ? { name: newName } : {}),
          };

          return updateFileMutation({ variables: { fileInfo: newFileInfo } });
        })
      );

      const failures = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      const successCount = results.length - failures.length;

      if (failures.length) {
        console.error("Error updating file type:", failures);
        handleApolloError(
          failures[0].reason,
          `${failures.length} of ${results.length} files couldn't be updated.`
        );
      }
      if (successCount) {
        loggedNotify.positive(
          axiosContext,
          Component.FILES,
          `Changed file type of ${successCount} file${
            successCount === 1 ? "" : "s"
          }`
        );
      }

      // Refresh whichever view is showing (folder or search) and drop the
      // stale selection.
      setCheckedMap({});
      refreshView();
    },
    [allFiles, updateFileMutation, axiosContext, refreshView]
  );

  const openFileTypeModal = React.useCallback(
    (value: Value) => {
      if (checkedFileIds.length === 0) {
        loggedNotify.negative(
          axiosContext,
          Component.FILES,
          "Select at least one file to change its type."
        );
        return;
      }
      setPendingFileType(value);
      setFileTypeChangeModalOpen(true);
    },
    [checkedFileIds, axiosContext]
  );

  // ── Move ───────────────────────────────────────────────────────────────────
  const [moveFilesToFolder] = useMutation(gql(moveFilesToFolderGql));
  // No onCompleted/onError: the move handlers report one combined outcome.
  const [moveFolders] = useMutation(gql(moveFoldersGql));

  // What the picker needs to know about the selection for copy or moving
  const moveOrCopyItems: MoveToItem[] = React.useMemo(
    () => [
      ...checkedFolders.map((folder) => ({
        id: String(folder.id),
        kind: "folder" as const,
        parentId: folderParentId(folder),
      })),
      ...checkedFiles.map((file) => ({
        id: String(file.id),
        kind: "file" as const,
        parentId: fileFolderId(file),
      })),
    ],
    [checkedFolders, checkedFiles, folderParentId, fileFolderId]
  );

  // One handler for any selection: folders and files both go to the chosen
  // destination. Items already there are skipped.
  const handleMoveConfirm = React.useCallback(
    async (destinationId: UUID | null) => {
      setMoveModalOpen(false);

      const destination = destinationId
        ? allFolders.find((folder) => folder.id === destinationId)?.name ??
          "folder"
        : ROOT_NAME;

      const movedFolders = checkedFolders.filter(
        (folder) => folderParentId(folder) !== destinationId
      );
      const movedFiles = checkedFiles.filter(
        (file) => fileFolderId(file) !== destinationId
      );

      if (movedFolders.length === 0 && movedFiles.length === 0) {
        loggedNotify.positive(
          axiosContext,
          Component.FILES,
          `Selected item${
            selectedCount === 1 ? " is" : "s are"
          } already in ${destination}`
        );
        return;
      }

      // One item → its name; several → "3 files", "2 folders" or "5 items".
      const names = [
        ...movedFolders.map((folder) => folder.name),
        ...movedFiles.map((file) => file.attributes?.name ?? "1 file"),
      ];
      const noun =
        movedFiles.length === 0
          ? "folders"
          : movedFolders.length === 0
          ? "files"
          : "items";
      const subject = describeItems(names, noun);

      try {
        if (movedFolders.length > 0) {
          await moveFolders({
            variables: {
              folderIds: movedFolders.map((folder) => folder.id),
              folderId: destinationId,
            },
          });
        }
        if (movedFiles.length > 0) {
          await moveFilesToFolder({
            variables: {
              fileIds: movedFiles.map((file) => file.id),
              folderId: destinationId,
            },
          });
        }
        // Only reached once every move has completed on the server.
        loggedNotify.positive(
          axiosContext,
          Component.FILES,
          `Moved ${subject} to ${destination}`
        );
      } catch (error) {
        handleApolloError(
          error as ApolloError,
          `Couldn't move ${subject} to ${destination}.`
        );
      } finally {
        // Refresh either way so a partial move is reflected.
        setCheckedMap({});
        await refreshView();
      }
    },
    [
      allFolders,
      checkedFolders,
      checkedFiles,
      folderParentId,
      fileFolderId,
      selectedCount,
      moveFolders,
      moveFilesToFolder,
      axiosContext,
      refreshView,
    ]
  );

  const handleMoveTo = React.useCallback(() => {
    if (selectedCount === 0) return;
    fetchAllFolders();
    setMoveModalOpen(true);
  }, [selectedCount, fetchAllFolders]);

  // ── Copy ───────────────────────────────────────────────────────────────────
  const [copyFolders] = useMutation(gql(CopyFolders));
  const [copyFilesToFolder] = useMutation(gql(copyFilesToFolderGql));

  const handleCopyConfirm = React.useCallback(
    async (destinationId: UUID | null) => {
      setCopyModalOpen(false);

      const destination = destinationId
        ? allFolders.find((folder) => folder.id === destinationId)?.name ??
          "folder"
        : ROOT_NAME;

      if (checkedFolders.length === 0 && checkedFiles.length === 0) return;

      const names = [
        ...checkedFolders.map((folder) => folder.name),
        ...checkedFiles.map((file) => file.attributes?.name ?? "1 file"),
      ];
      const noun =
        checkedFiles.length === 0
          ? "folders"
          : checkedFolders.length === 0
          ? "files"
          : "items";
      const subject = describeItems(names, noun);

      try {
        if (checkedFolders.length > 0) {
          await copyFolders({
            variables: {
              folderIds: checkedFolders.map((folder) => folder.id),
              folderId: destinationId,
            },
          });
        }
        if (checkedFiles.length > 0) {
          await copyFilesToFolder({
            variables: {
              fileIds: checkedFiles.map((file) => file.id),
              folderId: destinationId,
            },
          });
        }

        loggedNotify.positive(
          axiosContext,
          Component.FILES,
          `Copied ${subject} to ${destination}`
        );
      } catch (error) {
        handleApolloError(
          error as ApolloError,
          `Couldn't copy ${subject} to ${destination}.`
        );
      } finally {
        // Refresh either way so a partial copy is reflected.
        setCheckedMap({});
        await refreshView();
      }
    },
    [
      allFolders,
      checkedFolders,
      checkedFiles,
      copyFolders,
      copyFilesToFolder,
      axiosContext,
      refreshView,
    ]
  );

  const handleCopyTo = React.useCallback(() => {
    if (selectedCount === 0) return;
    fetchAllFolders();
    setCopyModalOpen(true);
  }, [selectedCount, fetchAllFolders]);

  // ── Folder navigation ──────────────────────────────────────────────────────
  const openFolder = React.useCallback((folder: Folder) => {
    setSearchValue("");
    setCheckedMap({});
    setCurrentPage(1);
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
  }, []);

  // index = -1 navigates to the root; otherwise to that crumb.
  const navigateToCrumb = React.useCallback((index: number) => {
    setSearchValue("");
    setCheckedMap({});
    setCurrentPage(1);
    setFolderPath((prev) => (index < 0 ? [] : prev.slice(0, index + 1)));
  }, []);

  // ── Create folder ──────────────────────────────────────────────────────────
  const [createFolder] = useMutation(gql(createFolderGql), {
    onCompleted: () => {
      loggedNotify.positive(axiosContext, Component.FILES, "Folder created.");
      setFolderModalOpen(false);
      refreshView();
    },
    onError: (error) => handleApolloError(error, "Error creating folder."),
  });

  const handleCreateFolder = React.useCallback(
    (name: string) => {
      createFolder({ variables: { name, parentId: currentFolderId } });
    },
    [createFolder, currentFolderId]
  );

  // ── Delete selected items (files + folders) ────────────────────────────────
  // Success handling is centralized in handleDelete so the two mutations report
  // a single outcome; leaving onError off lets a failed await reject into the
  // try/catch below (e.g. a folder refused because a file is in a saved mapping).
  const [removeFiles] = useMutation(gql(removeFilesGql));
  const [deleteFolder] = useMutation(gql(deleteFolderGql));

  const handleDelete = React.useCallback(async () => {
    setDeletePromptIsOpen(false);
    try {
      if (checkedFileIds.length > 0) {
        await removeFiles({ variables: { fileIds: checkedFileIds } });
      }
      // Each selected folder is deleted on its own (deleteFolder also removes
      // the folder's files); one at a time so a refusal points at the folder.
      for (const folderId of checkedFolderIds) {
        await deleteFolder({ variables: { folderId } });
      }
      loggedNotify.positive(
        axiosContext,
        Component.FILES,
        "Deleted successfully"
      );
    } catch (error) {
      loggedNotify.negative(
        axiosContext,
        Component.FILES,
        `Error deleting: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
      console.error("Error during deletion:", error);
    } finally {
      // Refresh either way so a partial delete (some items removed before a
      // refusal) is reflected, and drop the stale selection.
      setCheckedMap({});
      refreshView();
    }
  }, [
    checkedFileIds,
    checkedFolderIds,
    removeFiles,
    deleteFolder,
    refreshView,
    axiosContext,
  ]);

  // After an upload commits, files land unfiled by default; if the user is
  // inside a folder, move the newly uploaded files into it.
  const handleUploaded = React.useCallback(
    async (uploaded: UploadedFile[]) => {
      if (currentFolderId && uploaded.length > 0) {
        try {
          await moveFilesToFolder({
            variables: {
              fileIds: uploaded.map((file) => file.id),
              folderId: currentFolderId,
            },
          });
        } catch (error) {
          handleApolloError(
            error as ApolloError,
            "Files uploaded, but could not be moved into this folder."
          );
        }
      }
      refreshView();
    },
    [currentFolderId, moveFilesToFolder, refreshView]
  );

  // Placeholder for selection actions that aren't wired yet
  // They surface a toast so the buttons are obviously inert rather than silently doing nothing.
  const notifyNotImplemented = React.useCallback(
    (action: string) => {
      loggedNotify.negative(
        axiosContext,
        Component.FILES,
        `${action} isn't available yet.`
      );
    },
    [axiosContext]
  );

  // ── Rename folder ──────────────────────────────────────────────────────────
  const [renameFolder] = useMutation(gql(renameFolderGql), {
    onCompleted: () => {
      loggedNotify.positive(axiosContext, Component.FILES, "Folder renamed.");
      setRenameTarget(null);
      refreshView();
    },
    onError: (error) => handleApolloError(error, "Error renaming folder."),
  });

  // Rename applies to a single selected item (the action bar only shows Rename
  // when exactly one is selected): a folder opens the folder-rename modal, a
  // file opens the file-rename modal.
  const handleRename = React.useCallback(() => {
    if (checkedFolderIds.length === 1 && checkedFileIds.length === 0) {
      const folder = folders.find((f) => f.id === checkedFolderIds[0]);
      if (folder) {
        setRenameTarget(folder);
      }
      return;
    }
    if (checkedFileIds.length === 1 && checkedFolderIds.length === 0) {
      const file = allFiles.find((f) => f.id === checkedFileIds[0]);
      if (file) {
        setRenameFileTarget(file);
      }
      return;
    }
    notifyNotImplemented("Rename");
  }, [
    checkedFolderIds,
    checkedFileIds,
    folders,
    allFiles,
    notifyNotImplemented,
  ]);

  const handleRenameConfirm = React.useCallback(
    (newName: string) => {
      if (renameTarget && newName !== renameTarget.name) {
        renameFolder({
          variables: { folderId: renameTarget.id, newName },
        });
      } else {
        // No change (or nothing targeted) — just close the modal.
        setRenameTarget(null);
      }
    },
    [renameTarget, renameFolder]
  );

  // Renaming a file updates its name via the shared updateFileInfo mutation
  // (the file's attributes.name, which is what the table shows). Other type /
  // marking fields are passed through unchanged so they aren't cleared.
  const handleRenameFileConfirm = React.useCallback(
    async (newName: string): Promise<void> => {
      const file = renameFileTarget;

      if (!file || newName === file.attributes?.name) {
        setRenameFileTarget(null);
        return;
      }

      try {
        await updateFileMutation({
          variables: {
            fileInfo: {
              id: file.id,
              securityMarkings: file.securityMarkings,
              predictedFileType: file.predictedFileType,
              userSelectedFileType: file.userSelectedFileType,
              name: newName,
            },
          },
        });

        loggedNotify.positive(axiosContext, Component.FILES, "File renamed.");

        setRenameFileTarget(null);
        setCheckedMap({});
        refreshView();
      } catch (error) {
        handleApolloError(error as ApolloError, "Error renaming file.");
      }
    },
    [renameFileTarget, updateFileMutation, axiosContext, refreshView]
  );

  // ── Download selection ───────────────────────────────────────────────────────
  // A single file downloads directly (unzipped); anything else — one or more
  // folders, several files, or a mix — is zipped server-side and streamed back.
  const handleDownload = React.useCallback(async () => {
    const onlySingleFile =
      checkedFolderIds.length === 0 && checkedFileIds.length === 1;

    try {
      if (onlySingleFile) {
        const fileId = checkedFileIds[0];
        const fileName =
          allFiles.find((file) => file.id === fileId)?.attributes?.name ??
          "file";
        const response = await axiosContext.get(
          `${Endpoints.DownloadFile}=${fileId}`,
          { responseType: "blob" }
        );
        // The endpoint answers 200 even when it refuses; catch the JSON error
        // body so we toast instead of saving it as the file.
        const errorMessage = await readDownloadErrorMessage(response.data);
        if (errorMessage) {
          loggedNotify.negative(
            axiosContext,
            Component.FILES,
            `Could not download ${fileName}: ${errorMessage}`
          );
          return;
        }
        fileDownload(response.data, fileName);
        return;
      }

      const response = await axiosContext.post(
        Endpoints.DownloadZip,
        { folderIds: checkedFolderIds, fileIds: checkedFileIds },
        { responseType: "blob" }
      );
      const errorMessage = await readDownloadErrorMessage(response.data);
      if (errorMessage) {
        loggedNotify.negative(
          axiosContext,
          Component.FILES,
          `Could not download: ${errorMessage}`
        );
        return;
      }
      // A lone folder keeps its own name; everything else is a generic archive.
      const zipName =
        checkedFolderIds.length === 1 && checkedFileIds.length === 0
          ? `${
              folders.find((folder) => folder.id === checkedFolderIds[0])
                ?.name ?? "folder"
            }.zip`
          : "dexter_download.zip";
      fileDownload(response.data, zipName, "application/zip");
    } catch (error) {
      loggedNotify.negative(
        axiosContext,
        Component.FILES,
        `Could not download: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }, [checkedFolderIds, checkedFileIds, axiosContext, allFiles, folders]);

  const UPLOAD_STEP_RESERVED_HEIGHT = 720;
  // Minimum upload row height
  const MIN_LIST_HEIGHT = 225;

  const uploadListMaxHeight = useListMaxHeight(
    UPLOAD_STEP_RESERVED_HEIGHT,
    MIN_LIST_HEIGHT
  );

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          borderBottom: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
          backgroundColor: currentTheme.colors.pageBackgroundColor,
          padding: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "15px",
            marginTop: "10px",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <HeaderBreadcrumb
            folderPath={folderPath}
            onNavigate={navigateToCrumb}
          />
        </div>
        <div
          style={{
            marginLeft: "15px",
            marginRight: "15px",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <OverflowMenu overflowActions={[]} />
        </div>
      </div>

      {/* Hides the demo files from the list. Shows only user uploaded files */}
      {showEmptyState ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: currentTheme.colors.pageBackgroundColor,
            color: currentTheme.colors.text,
          }}
        >
          <EmptyPageComponent
            icon={FiUpload}
            title="No files yet"
            description="Upload a file or drag and drop to get started"
            actions={
              <EmptyStateButton
                id="upload-file-empty-state-button"
                icon={FiUpload}
                onClick={() => setUploadOpen(true)}
              >
                Upload File
              </EmptyStateButton>
            }
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            padding: currentTheme.spacing.md,
            backgroundColor: currentTheme.colors.pageBackgroundColor,
            color: currentTheme.colors.text,
            flex: 1,
            minHeight: 0,
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {fileTypeChangeModalOpen && (
            <FileTypeModal
              isOpen={fileTypeChangeModalOpen}
              items={checkedFiles.map((file) => ({
                id: String(file.id),
                name: file.attributes?.name ?? "",
                format:
                  file.userSelectedFileType ||
                  file.predictedFileType ||
                  "UNDETECTED",
              }))}
              fileTypeOptions={fileTypeOptionsFilesTable}
              initialFileType={pendingFileType}
              onClose={() => setFileTypeChangeModalOpen(false)}
              onConfirm={handleFormatChange}
            />
          )}

          {/* Vertical Spacer */}
          <div style={{ height: currentTheme.spacing.xxl }} />

          {/* Actions bar for table. Shows the default tools, or selection
              actions while one or more files are checked. */}
          <TableActionBar
            style={{
              marginLeft: "25px",
              marginRight: "25px",
              marginBottom: currentTheme.spacing.md,
              boxSizing: "border-box",
              minHeight: ACTION_BAR_MIN_HEIGHT,
              // While items are selected the bar takes the selected-item blue,
              // matching a selected row (same selectionBackgroundPrimary token).
              ...(selectedCount > 0
                ? {
                    backgroundColor:
                      currentTheme.colors.selectionBackgroundPrimary,
                  }
                : {}),
            }}
          >
            {selectedCount > 0 ? (
              <TableSelectActionsBar
                selectedCount={selectedCount}
                onRename={handleRename}
                onMoveTo={handleMoveTo}
                onCopyTo={handleCopyTo}
                onDownload={handleDownload}
                onDelete={() => setDeletePromptIsOpen(true)}
                onFileTypeChange={openFileTypeModal}
              />
            ) : (
              <>
                <Input
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.currentTarget.value)}
                  placeholder="Find..."
                  startEnhancer={
                    <Search
                      size={currentTheme.iconSizes.default}
                      color={currentTheme.colors.secondaryTextColor}
                    />
                  }
                  overrides={SEARCH_INPUT_OVERRIDES}
                />
                <Button
                  data-testid="upload-file-button"
                  onClick={() => setUploadOpen(true)}
                  overrides={{
                    ...getSecondaryButtonOverrides(currentTheme, {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    }),
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiUpload /> Upload file
                  </span>
                </Button>
                <Button
                  onClick={() => setFolderModalOpen(true)}
                  overrides={{
                    ...getSecondaryButtonOverrides(currentTheme, {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    }),
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiFolderPlus size={16} />
                    New folder
                  </span>
                </Button>
              </>
            )}
          </TableActionBar>

          {/* Dismissible saved-mapping banner, same width as the toolbar. */}
          {!bannerDismissed && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: currentTheme.spacing.sm,
                marginLeft: "25px",
                marginRight: "25px",
                marginBottom: currentTheme.spacing.md,
                padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`,
                boxSizing: "border-box",
                backgroundColor: currentTheme.colors.selectionBackgroundPrimary,
                border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
                borderRadius: "8px",
              }}
            >
              <span
                style={{
                  fontFamily: currentTheme.fonts.primary,
                  fontSize: currentTheme.fontSizes.small,
                  color: currentTheme.colors.secondaryTextColor,
                }}
              >
                Files associated with one or more saved mappings or the demo
                mapping cannot be removed.
              </span>
              <button
                type="button"
                aria-label="Dismiss"
                title="Dismiss"
                onClick={dismissBanner}
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: currentTheme.colors.secondaryTextColor,
                  padding: currentTheme.spacing.xs,
                  flexShrink: 0,
                }}
              >
                <FiX size={16} />
              </button>
            </div>
          )}

          {/* Uploaded files + folders table */}
          <FileSearchResultTable
            files={allFiles}
            folders={folders}
            onOpenFolder={openFolder}
            onOpenFile={setViewingFile}
            currentPage={safePage}
            pageSize={pageSize}
            checkedMap={checkedMap}
            onCheckedMapChange={handleCheckedMapChange}
            onSortChange={handleSortChange}
            emptyMessage={tableEmptyMessage}
          />

          {/* PaginationControls */}
          {!loading && listableCount > 0 && (
            <FileTablePagination
              currentPage={safePage}
              numPages={pageCount}
              onPageChange={handlePageChange}
            />
          )}

          <DeleteConfirmationModal
            isOpen={deletePromptIsOpen}
            header="Delete"
            bodyText={`Are you sure you want to delete ${selectedCount} item${
              selectedCount !== 1 ? "s" : ""
            }?${
              checkedFolderIds.length > 0
                ? " Deleting a folder also deletes its contents."
                : ""
            }`}
            onDelete={handleDelete}
            onCancel={() => setDeletePromptIsOpen(false)}
          />
        </div>
      )}
      <MoveToModal
        isOpen={moveModalOpen}
        items={moveOrCopyItems}
        folders={allFolders}
        onConfirm={handleMoveConfirm}
        startFolderId={currentFolderId}
        onCancel={() => setMoveModalOpen(false)}
      />
      <CopyToModal
        isOpen={copyModalOpen}
        items={moveOrCopyItems}
        folders={allFolders}
        onConfirm={handleCopyConfirm}
        startFolderId={currentFolderId}
        onCancel={() => setCopyModalOpen(false)}
      />
      {viewingFile && (
        <NodeFileDataModal
          isOpen={viewingFile !== null}
          header={viewingFile.attributes?.name ?? "File"}
          fileType={
            viewingFile.userSelectedFileType ??
            viewingFile.predictedFileType ??
            FileType.UNDETECTED
          }
          fileId={viewingFile.id}
          onClose={() => setViewingFile(null)}
        />
      )}
      <UploadModal
        listMaxHeight={uploadListMaxHeight}
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        uploadFile={uploadFile}
        removeFile={removeUploadedFile}
        onError={(message) =>
          loggedNotify.negative(axiosContext, Component.FILES, message)
        }
        onUploaded={handleUploaded}
      />
      <NamePromptModal
        isOpen={folderModalOpen}
        title="New folder"
        confirmLabel="Confirm"
        onConfirm={handleCreateFolder}
        onCancel={() => setFolderModalOpen(false)}
      />
      <NamePromptModal
        isOpen={renameTarget !== null}
        title="Rename folder"
        confirmLabel="Rename"
        initialName={renameTarget?.name ?? ""}
        onConfirm={handleRenameConfirm}
        onCancel={() => setRenameTarget(null)}
      />
      <NamePromptModal
        isOpen={renameFileTarget !== null}
        title="Rename file"
        confirmLabel="Rename"
        initialName={renameFileTarget?.attributes?.name ?? ""}
        onConfirm={handleRenameFileConfirm}
        onCancel={() => setRenameFileTarget(null)}
      />
    </div>
  );
}
